type Props ={
  setScrolling: (state: boolean) => void;
  setConnecting: (state: boolean) => void;
  setRunCountDown: () => void;
  setAverageCalibrateLevel: (state: number) => void;
  setCalibratingAudioService: (state: boolean) => void;
  averageLevel: number;
  showNotification: (message: string) => void;
  setPlayPrompterHandler: (value: boolean) => void;
  audioId: string;
};

class AudioConnector {
  protected audioContext: AudioContext | null;

  protected showNotification: (message: string) => void;

  protected setPlayPrompterHandler: (value: boolean) => void;

  public mediaStream: MediaStream | null;

  protected mediaSource: MediaStreamAudioSourceNode | null;

  protected scriptProcessor: ScriptProcessorNode | null;

  protected analyzer: AnalyserNode | null;

  protected isConnect: boolean;

  constructor(protected audioId: string) {
    this.audioContext = null;
    this.mediaStream = null;
    this.mediaSource = null;
    this.scriptProcessor = null;
    this.analyzer = null;
    this.audioId = audioId;
    this.isConnect = false;
    this.showNotification = () => {};
    this.setPlayPrompterHandler = (value: boolean) => {};
  }

  protected errorOccurred(message : string) {
    console.log(message);
  }

  protected connect(callback: () => void) {
    try {
      navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: false,
          echoCancellation: false,
          noiseSuppression: true,
          deviceId: this.audioId,
        },
      })
        .then((stream) => {
          // grab an audio context
          const audioContext = ((window as any).AudioContext || (window as any).webkitAudioContext);
          this.audioContext = (new (audioContext as any)()) as AudioContext;
          if (this.mediaStream) {
            const tracks = this.mediaStream.getTracks();
            if (tracks.length > 0) {
              tracks.forEach((track, i) => {
                track.stop();
                if (this.mediaStream) {
                  this.mediaStream.removeTrack(tracks[i]);
                }
              });
            }
          }
          this.mediaStream = stream;
          this.analyzer = this.audioContext.createAnalyser();
          this.analyzer.smoothingTimeConstant = 0.5;
          this.analyzer.fftSize = 1024;

          this.mediaSource = this.audioContext.createMediaStreamSource(this.mediaStream);
          this.scriptProcessor = this.audioContext.createScriptProcessor(2048, 1, 1);

          this.mediaSource.connect(this.analyzer);
          this.analyzer.connect(this.scriptProcessor);
          this.scriptProcessor.connect(this.audioContext.destination);
          this.isConnect = true;
          callback();
        })
        .catch((err) => {
          this.showNotification('Microphone not included or no permissions to use it');
          this.setPlayPrompterHandler(false);
          this.errorOccurred(`The following error occurred: ${err.name}`);
        });
    } catch (e) {
      this.showNotification('Microphone not included or no permissions to use it');
      this.setPlayPrompterHandler(false);
      console.log(e);
    }
  }

  static checkAudioSupport = (resolve: () => void, reject: () => void) => {
    try {
      if (navigator.mediaDevices) {
        resolve();
      } else {
        reject();
      }
    } catch (e) {
      reject();
      console.log('microphone not included or no permissions to use it');
    }
  };


  protected disconnect(callback: () => void) {
    try {
      if (this.mediaStream && this.audioContext && this.mediaSource) {
        const tracks = this.mediaStream.getTracks();
        if (tracks.length > 0) {
          tracks.forEach((track, i) => {
            track.stop();
            if (this.mediaStream) {
              this.mediaStream.removeTrack(tracks[i]);
            }
          });
        }
        this.mediaSource.disconnect();
        this.isConnect = false;
        if (this.audioContext.state !== 'closed') {
          this.audioContext.close().then(() => callback());
        } else {
          callback();
        }
      }
    } catch (e) {
      console.log(e);
    }
  }
}

class AudioService extends AudioConnector {
  protected canvas: HTMLCanvasElement | null;

  protected setScrolling: (state: boolean) => void;

  protected setRunCountDown: () => void;

  protected setConnecting: (state: boolean) => void;

  public setAudioId: (audioId: string) => void = (audioId) =>{
    this.audioId = audioId;
  }

  protected setAverageCalibrateLevel: (state: number) => void;

  protected setCalibratingAudioService: (state: boolean) => void;

  protected setMaxMeasuredLevel: (state: number) => void = (val)=>{
    this.maxMeasuredLevel = val;
    localStorage.setItem('maxMeasuredLevel', val.toString());
  }

  protected isScrolling: boolean;

  protected isCalibrating: boolean;

  protected calAudioLevelSum : number;

  protected calAudioLevelCount: number;

  protected averageLevel: number;

  protected maxMeasuredLevel : number;

  protected previousAverageLevel : number;

  protected audioLevelsHistory: number[];

  protected audioLevelsHistoryMaxLength : number;

  protected timeOut: number | null;

  protected noiseThresholdMultiplierInitial: number;

  protected noiseThresholdMultiplierSteadyState: number;

  protected calRangeHighThreshold: number;

  protected calRangeHighInitialCoefficient : number;

  protected calRangeHighSteadyStateCoefficient : number;

  protected averagingCoefficient: number;

  protected audioPercentOfMaxThreshold : number;

  protected timerAfterNotVoice: number;

  protected calibrateTimer: number;

  protected calibrateTimerId: number | null;

  protected debug: boolean;

  protected audioProcessCalibrateListener: () => void;

  protected currentAudioProcess?: () => void;

  constructor(props: Props) {
    super(props.audioId);

    this.canvas = null;
    this.audioId = props.audioId;
    this.setScrolling = props.setScrolling;
    this.showNotification = props.showNotification;
    this.setConnecting = props.setConnecting;
    this.setPlayPrompterHandler = props.setPlayPrompterHandler;
    this.setRunCountDown = props.setRunCountDown;
    this.calAudioLevelSum = 0;
    this.calAudioLevelCount = 0;
    this.setAverageCalibrateLevel = props.setAverageCalibrateLevel;
    this.setCalibratingAudioService = props.setCalibratingAudioService;
    this.averageLevel = props.averageLevel;
    this.maxMeasuredLevel = Number(localStorage.getItem('maxMeasuredLevel') || 0);
    this.isScrolling = false;
    this.isCalibrating = false;
    this.previousAverageLevel = 0.0;
    this.audioLevelsHistory = [];
    this.audioLevelsHistoryMaxLength = 50;
    this.noiseThresholdMultiplierInitial = 3.0;
    this.noiseThresholdMultiplierSteadyState = 2.0;
    this.calRangeHighThreshold = 20.0;
    this.calRangeHighInitialCoefficient = 0.5;
    this.calRangeHighSteadyStateCoefficient = 0.65;
    this.averagingCoefficient = 0.95;
    this.audioPercentOfMaxThreshold = 0.20;
    this.timerAfterNotVoice = 250;
    this.calibrateTimer = 3000;
    this.timeOut = null;
    this.calibrateTimerId = null;
    this.debug = false;
    this.audioProcessCalibrateListener = () => {};
  }

  public updateCalibrateData(averageCalibrateLevel: number) {
    this.averageLevel = averageCalibrateLevel;
  }

  protected errorOccurred(message: string) {
    if (this.debug) {
      alert(message);
    } else {
      super.errorOccurred(message);
    }
  }

  public startWithCountDown() {
    if (!this.isConnect) {
      this.connect(() => {
        this.audioLevelHistoryClear();
        if (this.averageLevel === 0) {
          this.calibrate(() => { this.setRunCountDown(); });
        } else {
          this.setRunCountDown();
        }
      });
    }
  }

  public startProcessingVoice() {
    this.currentAudioProcess = this.audioProcessListener.bind(this);
    this.scriptProcessor?.addEventListener('audioprocess', this.currentAudioProcess);
    this.setConnecting(true);
  }

  public stopProcessingVoice() {
    if (this.currentAudioProcess) {
      this.scriptProcessor?.removeEventListener('audioprocess', this.currentAudioProcess);
    }
  }

  public startProcessingVoiceCameraMode() {
    this.currentAudioProcess = this.audioProcessListenerWithoutScrolling.bind(this);
    if (!this.isConnect) {
      this.connect(() => {
        this.audioLevelHistoryClear();
        if (this.averageLevel === 0) {
          this.calibrate();
        }
        if (this.currentAudioProcess) {
          this.scriptProcessor?.addEventListener('audioprocess', this.currentAudioProcess);
          this.setConnecting(true);
        }
      });
    } else {
      this.scriptProcessor?.addEventListener('audioprocess', this.currentAudioProcess);
    }
  }

  public start() {
    this.currentAudioProcess = this.audioProcessListener.bind(this);
    if (!this.isConnect) {
      this.connect(() => {
        this.audioLevelHistoryClear();
        if (this.averageLevel === 0) {
          this.calibrate();
        }
        if (this.currentAudioProcess) {
          this.scriptProcessor?.addEventListener('audioprocess', this.currentAudioProcess);
          this.setConnecting(true);
        }
      });
    } else {
      this.scriptProcessor?.addEventListener('audioprocess', this.currentAudioProcess);
    }
  }

  public calibrate(callback: () => void = () => {}) {
    this.isCalibrating = true;
    this.setCalibratingAudioService(true);
    this.audioProcessCalibrateListener = this.audioProcessCalibrate.bind(this);
    this.scriptProcessor?.addEventListener('audioprocess', this.audioProcessCalibrateListener);
    this.calAudioLevelSum = 0;
    this.calAudioLevelCount = 0;
    this.averageLevel = 0.0;
    this.maxMeasuredLevel = 0.0;
    this.audioLevelHistoryClear();

    this.calibrateTimerId = window.setTimeout(
      () => {
        this.scriptProcessor?.removeEventListener('audioprocess', this.audioProcessCalibrateListener);
        this.isCalibrating = false;
        this.setCalibratingAudioService(false);

        this.averageLevel = this.calAudioLevelSum / this.calAudioLevelCount;

        this.setAverageCalibrateLevel(this.averageLevel);
        callback();
      },
      this.calibrateTimer,
    );
  }

  public cancelCalibrate() {
    if (this.calibrateTimerId) {
      this.scriptProcessor?.removeEventListener('audioprocess', this.audioProcessCalibrateListener);
      this.isCalibrating = false;
      this.setCalibratingAudioService(false);
      window.clearTimeout(this.calibrateTimerId);
    }
  }

  public close() {
    this.disconnect(() => {
      this.setConnecting(false);
      this.clearCanvas();
    });
    this.timeOut = null;
    this.isCalibrating = false;
  }

  public setDebug(debug: boolean) {
    this.debug = debug;
  }

  public setCanvas(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
  }

  /**
   * Clear out audio level history
   */
  protected audioLevelHistoryClear() {
    this.audioLevelsHistory = [];
  }

  /**
   * Adds to the audio level history, limits the history size, update the max-measured level based on the history
   *
   * @param level = audio level to operate on
   */
  protected audioLevelHistoryPush(level : number) {
    this.audioLevelsHistory.push(level);

    // If the sample count is above the history length, then slice the end
    if (this.audioLevelsHistory.length > this.audioLevelsHistoryMaxLength) {
      this.audioLevelsHistory = this.audioLevelsHistory
        .slice(this.audioLevelsHistory.length - this.audioLevelsHistoryMaxLength);
    }

    let audioLevelHistoryMax = 0;

    // Update max
    this.audioLevelsHistory.forEach((levelValue) => {
      if (levelValue > audioLevelHistoryMax) {
        audioLevelHistoryMax = levelValue;
      }
    });

    // Update the measured max level
    this.maxMeasuredLevel = audioLevelHistoryMax;
    this.setMaxMeasuredLevel(audioLevelHistoryMax);
  }

  protected audioProcessListener() {
    if (this.isCalibrating) {
      if (this.isScrolling) {
        this.setScrolling(false);
        this.isScrolling = false;
      }
      return;
    }

    const analyser = this.analyzer;
    if (analyser !== null) {
      const array = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(array);

      const instantaneousLevel = this.calculateAverageVolume(array);

      // Check to see if the max measured level should be updated
      if (this.audioThresholdMet(instantaneousLevel) && (instantaneousLevel > this.maxMeasuredLevel)) {
        this.maxMeasuredLevel = instantaneousLevel;
        this.setMaxMeasuredLevel(instantaneousLevel);
      }

      // Average with the previous value
      const average = Math.max(instantaneousLevel, this.previousAverageLevel * this.averagingCoefficient);
      this.previousAverageLevel *= this.averagingCoefficient;

      if (this.audioThresholdMet(average)) {
        this.audioLevelHistoryPush(average);

        if (!this.isScrolling) {
          this.setScrolling(true);
          this.isScrolling = true;
        }

        if (this.timeOut !== null) {
          window.clearTimeout(this.timeOut);
          this.timeOut = null;
        }
      } else if (this.timeOut === null && this.isScrolling) {
        this.timeOut = window.setTimeout(() => {
          this.setScrolling(false);
          this.isScrolling = false;
          this.timeOut = null;
        }, this.timerAfterNotVoice);
      }
      this.drawCanvas(average);
    }
  }

  protected audioProcessListenerWithoutScrolling() {
    if (this.isCalibrating) {
      if (this.isScrolling) {
        this.setScrolling(false);
        this.isScrolling = false;
      }
      return;
    }

    const analyser = this.analyzer;
    if (analyser !== null) {
      const array = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(array);

      const instantaneousLevel = this.calculateAverageVolume(array);

      // Check to see if the max measured level should be updated
      if (this.audioThresholdMet(instantaneousLevel) && (instantaneousLevel > this.maxMeasuredLevel)) {
        this.maxMeasuredLevel = instantaneousLevel;
        this.setMaxMeasuredLevel(instantaneousLevel);
      }

      // Average with the previous value
      const average = Math.max(instantaneousLevel, this.previousAverageLevel * this.averagingCoefficient);
      this.previousAverageLevel *= this.averagingCoefficient;

      if (this.audioThresholdMet(average)) {
        this.audioLevelHistoryPush(average);

        if (this.timeOut !== null) {
          window.clearTimeout(this.timeOut);
          this.timeOut = null;
        }
      }
      this.drawCanvas(average);
    }
  }

  protected audioProcessCalibrate() {
    const analyser = this.analyzer;
    if (analyser !== null) {
      const array = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(array);
      const nextValue = this.calculateAverageVolume(array);

      this.calAudioLevelSum += nextValue;
      this.calAudioLevelCount++;

      if (this.debug) {
        this.drawCanvas(nextValue);
      }
    }
  }

  protected calculateAverageVolume(array: Uint8Array): number {
    let values = 0;
    let values2 = 0;
    const { length } = array;

    for (let i = 0; i < length; i += 1) {
      values += (array[i]);
    }
    const average = values / length;

    for (let i = 0; i < length; i += 1) {
      values2 += Math.pow((array[i] - average), 2);
    }

    return Math.sqrt(values2 / length);
  }

  /**
   * Determine meets audio threshold two ways:
   * 1) If "max measured level" is not set, then use only the noise threshold multiplier
   * 2) If "max measured level" is set: the use both the noise threshold and use a % of the max level
   *
   * @param volume = volume level sample to check
   */
  protected audioThresholdMet(volume : number) : boolean {
    const maxLevelHasBeenMeasured = (this.maxMeasuredLevel !== 0.0);
    if (maxLevelHasBeenMeasured) {
      if ((volume / this.maxMeasuredLevel) < this.audioPercentOfMaxThreshold) {
        return false;
      }
    }

    let noiseMultiplier = maxLevelHasBeenMeasured
      ? this.noiseThresholdMultiplierSteadyState : this.noiseThresholdMultiplierInitial;

    /*
      Special case where the calibration level is higher than usual, which happens on IOS and MacBook devices.
      Typical value is between 25.0 and 30.0

      In this case, the audio level with valid speaking is about 35-40, which would be about *1.5
     */
    if (this.averageLevel > this.calRangeHighThreshold) {
      // De-rate the multiplier in order to reduce the amplitude required to trigger
      noiseMultiplier *= maxLevelHasBeenMeasured ? this.calRangeHighSteadyStateCoefficient : this.calRangeHighInitialCoefficient;
    }

    return volume > (this.averageLevel * noiseMultiplier);
  }

  protected drawCanvas(volume: number) {
    if (this.canvas) {
      const canvasContext = this.canvas.getContext('2d');

      const width = (this.audioThresholdMet(volume))
        ? (volume / this.maxMeasuredLevel * this.canvas.width) : 0;

      if (canvasContext) {
        canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
        canvasContext.fillStyle = '#747C99';
        canvasContext.fillRect(0, 0, width, this.canvas.height);

        // Change the setting in the client, e.g. `AudioMeter`
        if (this.debug) {
          const yBase = 30;
          const xBase = 0;
          // Special audio-level debug code
          canvasContext.fillStyle = '#FFFFFF';
          canvasContext.font = '30px Courier';
          canvasContext.fillText(`Curr: ${volume.toFixed(6)}`, xBase, yBase);
          canvasContext.fillText(`Max: ${this.maxMeasuredLevel.toFixed(6)}`, xBase, yBase + 40);
          canvasContext.fillText(`Cal: ${this.averageLevel.toFixed(6)}`, xBase, yBase + 80);
        }
      }
    }
  }

  public clearCanvas() {
    if (this.canvas) {
      const canvasContext = this.canvas.getContext('2d');
      if (canvasContext) {
        canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
    }
  }

  static isSupportUserMedia(): boolean {
    return Boolean(navigator.mediaDevices);
  }
}


export default AudioService;
